// Pure helpers for the customer override editor. Extracted from the component
// so they can be unit-tested without rendering React.

import type { CatalogEntry, PolicyMode, ResolvedSetting } from './v2-settings-api';

export interface RowDraft {
  value: unknown;
  valueText: string; // for object/stringArray JSON editing
  policyMode: PolicyMode;
  policyReason: string;
  pendingClearValue?: boolean;
  pendingClearPolicy?: boolean;
}

export const NO_POLICY: PolicyMode = 'open';

// Convert a resolved value into a stable JSON textarea string for object/array types.
export const toText = (v: unknown): string => {
  if (v === undefined) return '';
  if (typeof v === 'string') return v;
  try { return JSON.stringify(v, null, 2); } catch { return String(v); }
};

// Parse JSON for object/stringArray inputs. Returns the original string if it
// doesn't parse — the server will reject it with a clear error.
export const parseJsonValue = (raw: string): unknown => {
  const t = raw.trim();
  if (t === '') return undefined;
  try { return JSON.parse(t); } catch { return raw; }
};

// Canonical comparison of values to detect a dirty draft. Object/array
// settings use JSON form since drafts store text for those types.
export const isValueDirty = (
  draft: RowDraft | undefined,
  resolved: ResolvedSetting | undefined,
  def: CatalogEntry,
): boolean => {
  if (!draft) return false;
  if (def.type === 'object' || def.type === 'stringArray') {
    return draft.valueText !== toText(resolved?.value);
  }
  return draft.value !== resolved?.value;
};

export const isPolicyDirty = (
  draft: RowDraft | undefined,
  resolved: ResolvedSetting | undefined,
  def: CatalogEntry,
): boolean => {
  if (!def.lockable || !draft) return false;
  const currentMode: PolicyMode = resolved?.lockedBy === 'customer' ? 'locked' : NO_POLICY;
  const currentReason = resolved?.reason ?? '';
  return draft.policyMode !== currentMode || (draft.policyMode !== NO_POLICY && draft.policyReason !== currentReason);
};

export const isRowDirty = (
  draft: RowDraft | undefined,
  resolved: ResolvedSetting | undefined,
  def: CatalogEntry,
): boolean => {
  if (!draft) return false;
  if (draft.pendingClearValue || draft.pendingClearPolicy) return true;
  return isValueDirty(draft, resolved, def) || isPolicyDirty(draft, resolved, def);
};

// Build the PATCH body for a single row from its current draft.
// Excludes any field being cleared (pendingClearValue / pendingClearPolicy),
// since the clear is handled by a DELETE issued first.
export const buildPatchBody = (
  draft: RowDraft | undefined,
  resolved: ResolvedSetting | undefined,
  def: CatalogEntry,
): { value?: unknown; policy?: { mode: PolicyMode; reason?: string } } => {
  const body: { value?: unknown; policy?: { mode: PolicyMode; reason?: string } } = {};
  if (!draft) return body;
  if (isValueDirty(draft, resolved, def) && !draft.pendingClearValue) {
    // stringArray always sends a real array: the chip editor writes JSON, and
    // legacy plain text is split into items rather than sent as one string.
    body.value = def.type === 'stringArray'
      ? parseStringArrayText(draft.valueText)
      : def.type === 'object'
        ? parseJsonValue(draft.valueText)
        : draft.value;
  }
  if (isPolicyDirty(draft, resolved, def) && !draft.pendingClearPolicy) {
    body.policy = { mode: draft.policyMode, ...(draft.policyReason ? { reason: draft.policyReason } : {}) };
  }
  return body;
};

// Determine the DELETE scope for a row, if any clears are staged.
export const deleteScopeFor = (
  draft: RowDraft | undefined,
): 'override' | 'policy' | 'both' | null => {
  if (!draft) return null;
  if (draft.pendingClearValue && draft.pendingClearPolicy) return 'both';
  if (draft.pendingClearValue) return 'override';
  if (draft.pendingClearPolicy) return 'policy';
  return null;
};

// Orchestrate per-row apply across an array of rows using Promise.allSettled,
// preserving input order in the failures list. Extracted so the index
// alignment between results[i] and rows[i] can be exercised by tests.
export async function saveAllRows<T>(
  rows: Array<[string, T]>,
  applyRow: (name: string, def: T) => Promise<void>,
): Promise<Array<{ name: string; error: unknown }>> {
  const results = await Promise.allSettled(rows.map(([n, d]) => applyRow(n, d)));
  return results
    .map((r, i) => ({ r, name: rows[i][0] }))
    .filter((x) => x.r.status === 'rejected')
    .map((x) => ({ name: x.name, error: (x.r as PromiseRejectedResult).reason }));
}

// ─── stringArray editing ─────────────────────────────────────────────────────
//
// stringArray settings (dispatch.callOnIncident.phoneNumbers / .emails) used to
// be edited as raw JSON text. Typing `+18669160918` instead of
// `["+18669160918"]` produced a string the server rejected, and the editor's
// refetch then wiped the draft — a silent no-op from the user's point of view
// (ticket 868m81zbq: Molina's PSOC number never saved, so a live dispatch
// called nobody). The row now edits a real list; these helpers convert between
// the draft's JSON text (kept so dirty-checks and PATCH bodies are unchanged)
// and the items shown as chips.

/** Split a typed or pasted value on commas, semicolons and newlines. */
export const splitListInput = (raw: string): string[] =>
  raw.split(/[,;\n]+/).map((s) => s.trim()).filter(Boolean);

/**
 * Items of a stringArray draft. Accepts the JSON array the editor writes and,
 * for text that isn't JSON (a value someone typed into the old textarea), a
 * plain comma/newline-separated list — so nothing typed is ever dropped.
 */
export const parseStringArrayText = (raw: string): string[] => {
  const t = (raw ?? '').trim();
  if (t === '') return [];
  try {
    const parsed = JSON.parse(t);
    const list = Array.isArray(parsed) ? parsed : [parsed];
    return list.map((v) => String(v).trim()).filter(Boolean);
  } catch {
    return splitListInput(t);
  }
};

/** Serialize items back to draft text; same formatting as toText so an
 *  unchanged list compares equal to the resolved value. */
export const stringArrayToText = (items: string[]): string => toText(items);

// The catalog's US phone pattern (dispatch.callOnIncident.phoneNumbers).
const E164_US_ITEM_REGEX = '^\\+1\\d{10}$';

/** Short noun for one item of a stringArray row, from its itemRegex. */
export const itemRegexLabel = (itemRegex?: string): string => {
  if (itemRegex === E164_US_ITEM_REGEX) return 'phone number';
  if (itemRegex && itemRegex.includes('@')) return 'email address';
  return 'value';
};

/** Longer description of the accepted format, for error text. */
export const itemRegexHint = (itemRegex?: string): string => {
  if (itemRegex === E164_US_ITEM_REGEX) return 'US phone number (stored as +1 and 10 digits, e.g. +18669160918)';
  if (itemRegex && itemRegex.includes('@')) return 'email address';
  if (itemRegex) return `value matching /${itemRegex}/`;
  return 'value';
};

/**
 * Normalize one item for the row's itemRegex. US phone lists accept numbers
 * the way people type them — "(866) 916-0918", "866.916.0918",
 * "1-866-916-0918" — and store them as +1XXXXXXXXXX. Other rows only trim.
 */
export const normalizeStringArrayItem = (item: string, itemRegex?: string): string => {
  const trimmed = item.trim();
  if (itemRegex === E164_US_ITEM_REGEX) {
    const digits = trimmed.replace(/\D/g, '');
    if (digits.length === 10) return `+1${digits}`;
    if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  }
  return trimmed;
};

/**
 * First problem with a stringArray's items, or null when the server will
 * accept them. Mirrors the stringArray rules in pom-2-api settings/validate.ts
 * (item regex, max length) so a bad list is caught before the PATCH, not after.
 */
export const validateStringArrayItems = (items: string[], def: CatalogEntry): string | null => {
  if (def.max !== undefined && items.length > def.max) {
    return `At most ${def.max} entries allowed (${items.length} entered).`;
  }
  if (def.itemRegex) {
    const re = new RegExp(def.itemRegex);
    const bad = items.find((item) => !re.test(item));
    if (bad !== undefined) return `"${bad}" is not a valid ${itemRegexHint(def.itemRegex)}.`;
  }
  return null;
};

/** Client-side validation of one row's draft; null when it can be sent. */
export const validateDraftValue = (draft: RowDraft | undefined, def: CatalogEntry): string | null => {
  if (!draft || draft.pendingClearValue) return null;
  if (def.type === 'stringArray') {
    return validateStringArrayItems(parseStringArrayText(draft.valueText), def);
  }
  if (def.type === 'object') {
    const t = (draft.valueText ?? '').trim();
    if (t === '') return null;
    try {
      JSON.parse(t);
      return null;
    } catch {
      return 'Not valid JSON.';
    }
  }
  return null;
};
